<template>
  <v-col cols="12" sm="6" md="6" lg="3">
    <v-card>
      <v-progress-linear
        color="primary"
        indeterminate
        v-if="$apollo.loading"
      ></v-progress-linear>
      <v-toolbar flat dense>
        <span class="text-subtitle-1"
          ><router-link
            class="unstyled-link"
            :to="`/org/${currentUser.organization.id}/fees`"
            >Fees in Period</router-link
          ></span
        >
        <v-spacer></v-spacer>
        <div class="avg-corner">
          <span class="avg-label">AVG</span>
          <span class="avg-value" :class="getAvgFeeTextClass()">{{
            feesAvgInPeriod
          }}</span>
        </div>
      </v-toolbar>
      <div class="stat-data">
        <div class="main-content">
          <span class="main-stat" :class="getFeesTextClass()">{{
            feesInPeriod
          }}</span>
          <span class="goal-text">/ {{ feesGoalFormatted }}</span>
        </div>
      </div>
    </v-card>
  </v-col>
</template>

<script>
import gql from "graphql-tag";
import { mapGetters } from "vuex";
import accounting from "accounting";
import differenceInBusinessDays from "date-fns/differenceInBusinessDays";
import addDays from "date-fns/addDays";

export default {
  apollo: {
    organization: {
      query: gql`
        query OrgHomeStats($id: ID!, $startDate: String!, $endDate: String!) {
          organization(id: $id) {
            id
            saFeesInPeriod(startDate: $startDate, endDate: $endDate)
            saFeesAvgInPeriod(startDate: $startDate, endDate: $endDate)
          }
        }
      `,
      variables() {
        return {
          id: this.$route.params.id,
          startDate: this.startDate,
          endDate: this.endDate
        };
      },
      skip() {
        return this.startDate === null || this.endDate === null;
      }
    }
  },
  computed: {
    ...mapGetters(["currentUser", "startDate", "endDate"]),
    feesInPeriod() {
      if (!this.organization) return accounting.formatMoney(0);
      return accounting.formatMoney(this.organization.saFeesInPeriod);
    },
    feesAvgInPeriod() {
      if (!this.organization) return accounting.formatMoney(0);
      return accounting.formatMoney(this.organization.saFeesAvgInPeriod);
    },
    businessDays() {
      let start = this.startDate.split("-").map(x => parseInt(x));
      let startDate = new Date(start[0], start[1] - 1, start[2]);
      let end = this.endDate.split("-").map(x => parseInt(x));
      let endDate = new Date(end[0], end[1] - 1, end[2]);
      // We only want to count business days up to today if range is current month
      // If historical range we will count business days through range
      if (endDate > new Date()) {
        endDate = new Date();
      }
      // Add a day to endDate so we can include the last day
      endDate = addDays(endDate, 1);

      return differenceInBusinessDays(endDate, startDate);
    },
    feesGoal() {
      // Weekly goal of $673,077 / 5 business days = $134,615 per business day
      // Based on $35M annual target / 52 weeks
      const dailyGoal = 673077 / 5;
      return this.businessDays * dailyGoal;
    },
    feesGoalFormatted() {
      return accounting.formatMoney(this.feesGoal);
    },
    feesRaw() {
      if (!this.organization) return 0;
      return this.organization.saFeesInPeriod;
    },
    avgFeeRaw() {
      if (!this.organization) return 0;
      return this.organization.saFeesAvgInPeriod;
    }
  },
  methods: {
    getFeesTextClass() {
      const percentage = (this.feesRaw / this.feesGoal) * 100;

      if (percentage >= 100) return "success--text";
      if (percentage >= 80) return "primary--text";
      if (percentage >= 60) return "warning--text";
      return "error--text";
    },
    getAvgFeeTextClass() {
      const avgFeeTarget = 15000;

      if (this.avgFeeRaw >= avgFeeTarget) return "success--text";
      if (this.avgFeeRaw >= avgFeeTarget * 0.8) return "primary--text";
      if (this.avgFeeRaw >= avgFeeTarget * 0.6) return "warning--text";
      return "error--text";
    }
  }
};
</script>

<style lang="scss">
.unstyled-link {
  color: rgba(0, 0, 0, 0.87) !important;
  text-decoration: none !important;

  &:hover {
    color: rgba(0, 0, 0, 0.6) !important;
    text-decoration: underline !important;
  }

  &:visited {
    color: rgba(0, 0, 0, 0.87) !important;
  }
}

// Enhanced typography for better readability
.stat-data {
  // Remove padding since avg is no longer positioned here
}

.avg-corner {
  display: flex;
  align-items: center;
  font-size: 0.75rem;
  gap: 4px;
  margin-right: 16px; // Add spacing from card edge
}

.avg-label {
  font-weight: 500 !important;
  color: rgba(0, 0, 0, 0.6) !important;
}

.avg-value {
  font-weight: 600 !important;
  font-size: 0.75rem;
}

.main-content {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 4px;
}

.main-stat {
  font-size: 1.75rem !important;
  font-weight: 600 !important;
  line-height: 1.2;
}

.goal-text {
  font-size: 0.875rem;
  font-weight: 500 !important;
  color: rgba(0, 0, 0, 0.6) !important;
}
</style>
